import { FocusForPerformerPopulationFocus } from '../../../coaching-base/general';
import { CoachingSwitchboardService } from '../../../coaching-base/switchboard';
import { FocusShallow } from '../../../coaching-tags/tags/tags.model';
import { epicDependencies } from '../../../store';
import {
    Cell,
    CellNode,
    Column,
    Row,
    RowNode,
    Sort,
    SortOrder,
} from '../grid/experimental.grid';
import {
    BehaviourDataPerMetric,
    FocusSelectorBehaviourModel,
} from '../model/focus-selector-behaviour.model';
import { FocusSelectorFocusForPerformer } from '../model/focus-selector-focus-for-performer.model';
import {
    BehaviourCellContext,
    BehaviourRow,
    CellContext,
    ColumnID,
    defaultSortDefs,
    FocusSelectionGrid,
    RowContext,
    SubBehaviourCellContext,
    TemplateID,
} from '../model/focus-selector-focus-selection.grid.model';
import { FocusSelectorMetricModel } from '../model/focus-selector-metric.model';
import { ProjectionEvaluationRangeId } from '../model/focus-selector-projection-evaluation-range.enum';
import {
    BehaviourMedal,
    DataPerMetricId,
    FocusSelectorSubBehaviourModel,
    SubBehaviourDataPerMetric,
} from '../model/focus-selector-subbehaviour.model';

function formatSubBehaviourDataPerMetric(
    fromUserBehaviour: Partial<FocusForPerformerPopulationFocus>,
    existing: FocusSelectorSubBehaviourModel,
): DataPerMetricId<SubBehaviourDataPerMetric> {
    const dataPerMetricId = { ...existing.dataPerMetricId };

    fromUserBehaviour.metrics?.forEach((e) => {
        if (e.metricId == null) {
            return;
        }

        if (dataPerMetricId[e.metricId] == null) {
            dataPerMetricId[e.metricId] = {
                rankData: {
                    rank: e.rankSubBehaviour,
                    medal: getMedal(e.rankSubBehaviour),
                },
                projectionResultPerRangeId: {},
            };
        }

        e.projections?.forEach((ee) => {
            dataPerMetricId[e.metricId].projectionResultPerRangeId[
                ee.evaluationRangeId
            ] = { projectionResult: ee.result };
        });
    });

    return dataPerMetricId;
}

function formatBehaviourDataPerMetric(
    fromUserBehaviour: Partial<FocusForPerformerPopulationFocus>,
    existing: FocusSelectorBehaviourModel,
): DataPerMetricId<BehaviourDataPerMetric> {
    const dataPerMetricId = { ...existing.dataPerMetricId };

    fromUserBehaviour.metrics?.forEach((e) => {
        if (e.metricId == null || dataPerMetricId[e.metricId] != null) {
            return;
        }

        dataPerMetricId[e.metricId] = {
            rankData: {
                rank: e.rankBehaviour,
                medal: getMedal(e.rankBehaviour),
            },
            topSubBehaviourId: null,
        };
    });

    return dataPerMetricId;
}

function getMedal(rank: number): BehaviourMedal {
    switch (rank) {
        case 1:
            return BehaviourMedal.GOLD;
        case 2:
            return BehaviourMedal.SILVER;
        case 3:
            return BehaviourMedal.BRONZE;
        default:
            return BehaviourMedal.NONE;
    }
}

function formatSubBehaviour(
    fromUserBehaviour: Partial<FocusForPerformerPopulationFocus>,
    existingSubBehaviour: FocusSelectorSubBehaviourModel,
    activeFocusesForPerformer: FocusSelectorFocusForPerformer[],
): FocusSelectorSubBehaviourModel {
    let formattedSubBehaviour = existingSubBehaviour;

    if (formattedSubBehaviour == null) {
        const { behaviourId, subBehaviourId } = fromUserBehaviour;

        const shallowFocus: FocusShallow = { behaviourId, subBehaviourId };
        const service =
            epicDependencies.injector.get<CoachingSwitchboardService>(
                CoachingSwitchboardService,
            );
        const hasServiceCommitment =
            service.pillarTwo.sbHasActiveCommitmentWithFocus(shallowFocus);
        const hasActiveCommitmentFromFocus = hasServiceCommitment
            ? false
            : Boolean(
                  activeFocusesForPerformer.find(
                      (focus) =>
                          focus.behaviourId === behaviourId &&
                          focus.tacticId === subBehaviourId,
                  ),
              );
        const hasActiveCommitment =
            hasServiceCommitment || hasActiveCommitmentFromFocus;
        formattedSubBehaviour = {
            id: fromUserBehaviour.subBehaviourId,
            identifier: `${fromUserBehaviour.behaviourId ?? 0}-${
                fromUserBehaviour.subBehaviourId ?? 0
            }`,
            name: fromUserBehaviour.subBehaviourFullName,
            hasActiveCommitment,
            dataPerMetricId: {},
        };
    }

    formattedSubBehaviour.dataPerMetricId = formatSubBehaviourDataPerMetric(
        fromUserBehaviour,
        formattedSubBehaviour,
    );

    return formattedSubBehaviour;
}

function formatBehaviour(
    fromUserBehaviour: Partial<FocusForPerformerPopulationFocus>,
    existing: FocusSelectorBehaviourModel,
): FocusSelectorBehaviourModel {
    let formattedBehaviour = existing;

    if (formattedBehaviour == null) {
        formattedBehaviour = {
            id: fromUserBehaviour.behaviourId,
            name: fromUserBehaviour.behaviourFullName,
            dataPerMetricId: {},
            subBehaviours: [],
        };
    }

    formattedBehaviour.dataPerMetricId = formatBehaviourDataPerMetric(
        fromUserBehaviour,
        formattedBehaviour,
    );

    return formattedBehaviour;
}

function getTopSubBehaviour(
    behaviour: FocusSelectorBehaviourModel,
    metricId: number,
): FocusSelectorSubBehaviourModel['id'] {
    return [...behaviour.subBehaviours]
        .filter((e) => e.dataPerMetricId[metricId]?.rankData?.rank != null)
        .sort((a, b) => {
            const aRank = a.dataPerMetricId[metricId].rankData.rank;
            const bRank = b.dataPerMetricId[metricId].rankData.rank;
            return aRank - bRank;
        })?.[0]?.id;
}

function assignTopSubBehaviours(
    behaviours: FocusSelectorBehaviourModel[],
): void {
    for (const behaviour of behaviours) {
        for (const metricId in behaviour.dataPerMetricId) {
            behaviour.dataPerMetricId[metricId] = {
                ...behaviour.dataPerMetricId[metricId],
                topSubBehaviourId: getTopSubBehaviour(
                    behaviour,
                    parseInt(metricId),
                ),
            };
        }
    }
}

export function formatUserBehaviours(
    userBehaviours: FocusForPerformerPopulationFocus[],
    activeFocusesForPerformer: FocusSelectorFocusForPerformer[],
): FocusSelectorBehaviourModel[] {
    const formattedBehaviours: FocusSelectorBehaviourModel[] = [];

    for (const userBehaviour of userBehaviours) {
        const existingBehaviour = formattedBehaviours.find(
            (formattedBehaviour) =>
                formattedBehaviour.id === userBehaviour.behaviourId,
        );

        const formattedBehaviour = formatBehaviour(
            userBehaviour,
            existingBehaviour,
        );

        if (existingBehaviour == null) {
            formattedBehaviours.push(formattedBehaviour);
        }

        const existingSubBehaviour = formattedBehaviour.subBehaviours.find(
            (subBehaviour) => subBehaviour.id === userBehaviour.subBehaviourId,
        );

        const formattedSubBehaviour = formatSubBehaviour(
            userBehaviour,
            existingSubBehaviour,
            activeFocusesForPerformer,
        );

        if (existingSubBehaviour == null) {
            formattedBehaviour.subBehaviours.push(formattedSubBehaviour);
        }
    }

    assignTopSubBehaviours(formattedBehaviours);

    return formattedBehaviours;
}

function defaultMinSafeInt(number?: number): number {
    return number != null ? number : Number.MAX_SAFE_INTEGER;
}

export function compare(
    cellA: CellNode,
    cellB: CellNode,
    rowA: RowNode<RowContext>,
    rowB: RowNode<RowContext>,
    def: Sort,
): number {
    const contextA = rowA.context.ctx.cellContext;
    const contextB = rowB.context.ctx.cellContext;
    let valA: number | string;
    let valB: number | string;

    switch (def.columnId) {
        case ColumnID.RANK:
            valA = defaultMinSafeInt(contextA.rank);
            valB = defaultMinSafeInt(contextB.rank);
            break;
        case ColumnID.FOCUS:
            valA = contextA.focus != null ? contextA.focus : '';
            valB = contextB.focus != null ? contextB.focus : '';
            break;
        case ColumnID.PROJECTED_15_DAY_IMPROVEMENT:
            valA = defaultMinSafeInt(contextA.projected15);
            valB = defaultMinSafeInt(contextB.projected15);
            break;
        case ColumnID.PROJECTED_30_DAY_IMPROVEMENT:
            valA = defaultMinSafeInt(contextA.projected30);
            valB = defaultMinSafeInt(contextB.projected30);
            break;
        case ColumnID.PROJECTED_60_DAY_IMPROVEMENT:
            valA = defaultMinSafeInt(contextA.projected60);
            valB = defaultMinSafeInt(contextB.projected60);
            break;
        default:
            return Number.MIN_SAFE_INTEGER;
    }

    if (typeof valA === 'string' && typeof valB === 'string') {
        return def.order === SortOrder.ASC
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
    }

    if (typeof valA === 'number' && typeof valB === 'number') {
        return def.order === SortOrder.ASC ? valA - valB : valB - valA;
    }

    return Number.MIN_SAFE_INTEGER;
}

export function makeColumns(): Column[] {
    return [
        {
            id: ColumnID.RANK,
            compareFn: compare,
            ctx: { templateId: TemplateID.HEADER },
            minWidth: 110,
        },
        {
            id: ColumnID.FOCUS,
            compareFn: compare,
            ctx: { templateId: TemplateID.HEADER },
            minWidth: 150,
        },
        {
            id: ColumnID.PROJECTED_15_DAY_IMPROVEMENT,
            compareFn: compare,
            ctx: { templateId: TemplateID.HEADER },
            minWidth: 110,
        },
        {
            id: ColumnID.PROJECTED_30_DAY_IMPROVEMENT,
            compareFn: compare,
            ctx: { templateId: TemplateID.HEADER },
            minWidth: 110,
        },
        {
            id: ColumnID.PROJECTED_60_DAY_IMPROVEMENT,
            compareFn: compare,
            ctx: { templateId: TemplateID.HEADER },
            minWidth: 110,
        },
    ];
}

export function makeCells(
    context: CellContext,
    isSelected?: boolean,
): Array<Cell<BehaviourCellContext | SubBehaviourCellContext>> {
    return [
        {
            columnId: ColumnID.RANK,
            ctx: {
                templateId: TemplateID.CONTENT,
                rank: context?.rank ?? null,
                medal: context?.medal ?? null,
                checked: isSelected,
            },
        },
        {
            columnId: ColumnID.FOCUS,
            ctx: {
                templateId: TemplateID.CONTENT,
                focus: context?.focus ?? '',
                checked: isSelected,
            },
        },
        {
            columnId: ColumnID.PROJECTED_15_DAY_IMPROVEMENT,
            ctx: {
                templateId: TemplateID.CONTENT,
                projected15: context?.projected15 ?? null,
                checked: isSelected,
            },
        },
        {
            columnId: ColumnID.PROJECTED_30_DAY_IMPROVEMENT,
            ctx: {
                templateId: TemplateID.CONTENT,
                projected30: context?.projected30 ?? null,
                checked: isSelected,
            },
        },
        {
            columnId: ColumnID.PROJECTED_60_DAY_IMPROVEMENT,
            ctx: {
                templateId: TemplateID.CONTENT,
                projected60: context?.projected60 ?? null,
                checked: isSelected,
            },
        },
    ];
}

export function makeChildRows(
    subBehaviours: FocusSelectorSubBehaviourModel[],
    activeMetricId?: FocusSelectorMetricModel['id'],
    selectedSubBehaviourId?: FocusSelectorSubBehaviourModel['id'],
    behaviourId?: number,
): Array<Row<RowContext, SubBehaviourCellContext>> {
    if (activeMetricId == null) {
        return [];
    }

    const rows: Array<Row<RowContext, SubBehaviourCellContext>> = [];

    subBehaviours.forEach((subBehaviour) => {
        const {
            id,
            identifier,
            name: focus,
            dataPerMetricId,
            hasActiveCommitment,
        } = subBehaviour;

        const dataForMetric = dataPerMetricId[activeMetricId];
        if (activeMetricId != null && dataForMetric == null) {
            return;
        }

        const rank = dataForMetric?.rankData?.rank;

        const medal = dataForMetric?.rankData?.medal;

        const projectionResultPerRangeId =
            dataForMetric?.projectionResultPerRangeId;

        const projected15 =
            projectionResultPerRangeId?.[ProjectionEvaluationRangeId.DAY_15]
                ?.projectionResult;

        const projected30 =
            projectionResultPerRangeId?.[ProjectionEvaluationRangeId.DAY_30]
                ?.projectionResult;

        const projected60 =
            projectionResultPerRangeId?.[ProjectionEvaluationRangeId.DAY_60]
                ?.projectionResult;

        const blFocus = { behaviourId, subBehaviourId: id };
        const cellContext = {
            id,
            rank,
            medal,
            focus,
            projected15,
            projected30,
            projected60,
            blFocus,
            hasActiveCommitment,
        };

        rows.push({
            id: identifier,
            ctx: {
                templateId: TemplateID.CONTENT,
                cellContext,
            },
            cells: makeCells(
                cellContext,
                id === selectedSubBehaviourId,
            ) as Array<Cell<SubBehaviourCellContext>>,
        });
    });

    return rows;
}

export function makeRows(
    behaviours: FocusSelectorBehaviourModel[],
    activeMetricId?: FocusSelectorMetricModel['id'],
    selectedSubBehaviourId?: FocusSelectorSubBehaviourModel['id'],
    filterByBehaviourId?: FocusSelectorBehaviourModel['id'],
    gridRows?: BehaviourRow[],
): BehaviourRow[] {
    if (activeMetricId == null) {
        return [];
    }

    const rows: BehaviourRow[] = [];

    const expandedStateMap = new Map<string, boolean>();
    if (gridRows != null && selectedSubBehaviourId != null) {
        gridRows.forEach((row) => {
            if (row.ctx.cellContext.id === selectedSubBehaviourId) {
                expandedStateMap.set(row.id, !row.isExpanded);
            }
        });
    }

    for (const behaviour of behaviours) {
        const { id, name: focus, subBehaviours, dataPerMetricId } = behaviour;

        if (filterByBehaviourId != null && id !== filterByBehaviourId) {
            continue;
        }

        const dataForMetric = dataPerMetricId[activeMetricId];
        if (activeMetricId != null && dataForMetric == null) {
            continue;
        }

        const rank = dataForMetric?.rankData?.rank;

        const medal = dataForMetric?.rankData?.medal;

        const topSubBehaviour = subBehaviours.find(
            (subBehaviour) =>
                subBehaviour.id === dataForMetric?.topSubBehaviourId,
        );

        const subBehaviourDataForMetric =
            topSubBehaviour?.dataPerMetricId?.[activeMetricId];

        const projected15 =
            subBehaviourDataForMetric?.projectionResultPerRangeId?.[
                ProjectionEvaluationRangeId.DAY_15
            ]?.projectionResult;

        const projected30 =
            subBehaviourDataForMetric?.projectionResultPerRangeId?.[
                ProjectionEvaluationRangeId.DAY_30
            ]?.projectionResult;

        const projected60 =
            subBehaviourDataForMetric?.projectionResultPerRangeId?.[
                ProjectionEvaluationRangeId.DAY_60
            ]?.projectionResult;

        const cellContext = {
            id,
            rank,
            medal,
            focus,
            projected15,
            projected30,
            projected60,
        };
        const isExpanded = subBehaviours?.some(
            (e) => expandedStateMap.get(e.id.toString()) === true,
        );

        rows.push({
            id: id.toString(),
            ctx: {
                templateId: TemplateID.CONTENT,
                cellContext,
            },
            cells: makeCells(cellContext) as Array<Cell<BehaviourCellContext>>,
            children: makeChildRows(
                subBehaviours,
                activeMetricId,
                selectedSubBehaviourId,
                id,
            ),
            isExpanded,
        });
    }

    return rows;
}

export function makeGrid(
    behaviours: FocusSelectorBehaviourModel[],
    activeMetricId?: FocusSelectorMetricModel['id'],
): FocusSelectionGrid {
    const columns = makeColumns();
    const rows = makeRows(behaviours, activeMetricId);
    return {
        columns,
        rows,
        sortDefs: defaultSortDefs,
        defaultSortDefs,
        gridRowsTop: [],
        rendererOpts: {
            columns,
            defaultRenderFn: null,
        },
    };
}

export function getSelectedBehaviourIdFromGrid(
    gridRows: BehaviourRow[],
): FocusSelectorBehaviourModel['id'] {
    for (const gridRow of gridRows) {
        if (gridRow.children != null) {
            for (const childRow of gridRow.children) {
                if (childRow.cells?.[0].ctx?.checked === true) {
                    return gridRow.ctx?.cellContext?.id;
                }
            }
        }
    }

    return null;
}

export function getBehaviourById(
    id: FocusSelectorBehaviourModel['id'],
    behaviours: FocusSelectorBehaviourModel[],
): FocusSelectorBehaviourModel {
    const foundBehaviour = behaviours.find((behaviour) => behaviour.id === id);

    return foundBehaviour ?? null;
}

export function getSubBehaviourById(
    id: FocusSelectorSubBehaviourModel['id'],
    behaviourId: FocusSelectorBehaviourModel['id'],
    behaviours: FocusSelectorBehaviourModel[],
): FocusSelectorSubBehaviourModel {
    const foundBehaviour = getBehaviourById(behaviourId, behaviours);

    if (foundBehaviour == null) {
        return null;
    }

    const foundSubBehaviour = foundBehaviour.subBehaviours.find(
        (subBehaviour) => subBehaviour.id === id,
    );

    return foundSubBehaviour ?? null;
}
